// How much of each widget on an overview page shows before the reader asks for more.
//
// Twins, ported one-for-one and checked against the same contract
// (spec/fixtures/overview_preview/expected.json, written by generate.py there):
//   web Ruby  app/services/overview_section_preview.rb  (server-rendered pages)
//   Android   OverviewSectionPreview.kt
//   iOS       OverviewSectionPreview.swift
//
// Every widget is capped by default and never opens expanded. A big widget with
// more of the page below it shows a preview and a "See all" link to a page of its
// own; a widget that is last on its page (or small) shows a preview and reveals the
// rest in place with "Show more", then folds back with "Show less".

export type OverviewSection =
  | 'peaks_on_route'
  | 'hike_list_hikes'
  | 'comments'
  | 'nearby_hikes'
  | 'peaks_bagged'
  | 'summit_hikes'
  | 'poi_reviews';

export type OverviewOverflow = 'see_all_page' | 'expand_in_place';

export type OverviewPreviewAction = 'none' | 'see_all' | 'show_more' | 'show_less';

export interface OverviewPreview {
  shown: number;
  action: OverviewPreviewAction;
}

export const OVERVIEW_SECTIONS: Readonly<Record<OverviewSection, { preview: number; overflow: OverviewOverflow }>> = Object.freeze({
  peaks_on_route: { preview: 4, overflow: 'see_all_page' },
  hike_list_hikes: { preview: 3, overflow: 'expand_in_place' },
  comments: { preview: 15, overflow: 'see_all_page' },
  nearby_hikes: { preview: 3, overflow: 'expand_in_place' },
  peaks_bagged: { preview: 4, overflow: 'expand_in_place' },
  summit_hikes: { preview: 5, overflow: 'expand_in_place' },
  poi_reviews: { preview: 3, overflow: 'expand_in_place' },
});

// Not item counts, but the same "capped by default" rule.
export const OVERVIEW_LINES = Object.freeze({
  /** Any long text: a description, each review body. */
  description_lines: 3,
  /** Tag chips; the rest fold into a "+N" chip. */
  tag_rows: 2,
});

/** How many of `total` items to show, and what sits under the list. */
export function overviewSectionPreview(section: OverviewSection, total: number, expanded: boolean): OverviewPreview {
  const { preview: cap, overflow } = OVERVIEW_SECTIONS[section];
  if (total <= cap) return { shown: total, action: 'none' };
  // A See all page never grows in place, so `expanded` means nothing here.
  if (overflow === 'see_all_page') return { shown: cap, action: 'see_all' };
  return expanded ? { shown: total, action: 'show_less' } : { shown: cap, action: 'show_more' };
}
